import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Search, User, X, ChevronDown, ChevronUp, ChevronRight, Lock, Mail } from "lucide-react";

import styles from "./RightSidebar.module.css";
import EmptyProfileImage from '../../../../../images/navbar/empty-profile-image.png';

// API Services
import { getActiveHomeUserData } from "../../../Services/getActiveHomeUserData";
import { getBusinessProfileData } from "../../../../HomeMessage/Services/getBusinessProfileData";
import { getSearchMessages } from "../../../Services/getSearchMessages";

// Child Components
import StarRating from "../../../../StarRating/startRating";
import HomeUserProfilePureCSS from '../../../../../profiles/HomeProfilePureCSS';
import BusinessProfilePureCSS from '../../../../../profiles/BusinessProfilePureCSS';

import UpgradeModal from '../../../../Common/UpgradeModal'; 

// Helper function to build the address safely
const formatAddress = (model) => {
    const addressParts = [
        model.streetNumber, model.streetName, model.suburb, model.myState, model.postCode
    ];
    return addressParts.filter(part => part).join(', ');
};

const formatReviewText = (count) => {
    if (!count && count !== 0) return '';
    return `(${count} review${count !== 1 ? 's' : ''})`;
};

function RightSidebar({ conversationSearchId, setChatSearching, activeConversation, homeData, isViewerSubscribed = false }) {

    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // --- MODAL & UI STATE ---
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isBioExpanded, setIsBioExpanded] = useState(false);

    // --- SEARCH STATE ---
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);

    useEffect(() => {
        if (!activeConversation || !homeData?.userId) {
            setProfileData(null);
            return;
        }

        const fetchProfileData = async () => {
            setLoading(true);
            setError(null);
            setProfileData(null);
            setIsSearchActive(false);
            setIsBioExpanded(false);
            setSearchText("");
            setFilteredMessages([]);

            try {
                if (homeData.userType === 'Home') {
                    const response = await getActiveHomeUserData(homeData.userId);
                    const { user } = response;

                    //console.log("user profileData = ", user);

                    // --- DEVELOPER LOGIC ---
                    const devName = user.developerBusinessName;
                    const isDeveloper = !!devName;

                    let userTagline = "Homeowner";
                    // Default home user: usually just Suburb/Postcode for privacy, but inside a chat we might show full address?
                    // For now, let's use the full formatted address if available, else postcode.
                    let userAddress = formatAddress(user) || user.postcode; 
                    let userPhone = user.phone; // Home users usually have phone hidden, but in a chat context, it might be visible.
                    let userEmail = user.email;

                    if (isDeveloper) {
                        userTagline = "Developer";
                    }

                    setProfileData({
                        profileType: 'Home',
                        id: homeData.userId,
                        name: isDeveloper ? `${user.firstName} ${user.lastName} (${devName})` : `${user.firstName} ${user.lastName}`,
                        profilePic: user.image,
                        tagline: userTagline,
                        address: userAddress, // Standardized key to 'address'
                        phone: userPhone,
                        email: userEmail,
                        isVerified: true // Home users/Devs are considered "Verified" for visibility purposes
                    });

                    

                } else if (homeData.userType === 'Business') {
                    const response = await getBusinessProfileData(homeData.userId);
                    const { model } = response;

                    setProfileData({
                        profileType: 'Business',
                        id: model.businessId,
                        name: model.businessName,
                        profilePic: model.profilePic,
                        tagline: model.businessDescription || model.overview,
                        bio: model.businessBio,
                        rating: model.overallRating,
                        reviewCount: model.reviewCount,
                        googleRating: model.googleRating,
                        googleRatingCount: model.googleRatingCount,
                        address: formatAddress(model),
                        phone: model.phone,
                        email: model.email,
                        isVerified: model.isVerified,
                        isDocApproved: model.isDocApproved
                    });
                }

                

            } catch (err) {
                console.error("Failed to fetch profile data:", err);
                setError("Could not load profile details.");
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [activeConversation, homeData?.userId, homeData?.userType]);

    // ... (Search Handlers remain the same)
    const handleToggleSearch = () => {
        setIsSearchActive(!isSearchActive);
        setSearchText("");
        setFilteredMessages([]);
        setSearchAttempted(false);
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (!searchText.trim()) return;
        setSearchLoading(true);
        setSearchAttempted(true);
        try {
            const results = await getSearchMessages(conversationSearchId, searchText);
            setFilteredMessages(results?.searchResponse || []);
        } catch (error) {
            console.error("Failed to fetch search results:", error);
            setFilteredMessages([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const BIO_MAX_LENGTH = 180;
    const isBioLong = profileData?.bio && profileData.bio.length > BIO_MAX_LENGTH;

    const getBioText = () => {
        if (!profileData?.bio) return '';
        if (isBioLong && !isBioExpanded) {
            return `${profileData.bio.substring(0, BIO_MAX_LENGTH)}...`;
        }
        return profileData.bio;
    };

    // --- MASKING LOGIC HELPERS ---


    const shouldMaskDetails = () => {

        if (!profileData) return false;

        if (profileData.profileType === 'Home') {
            // "Paywall Lock": I must pay to see the lead's details
            return !isViewerSubscribed;
        } 
        
        if (profileData.profileType === 'Business') {
            // "Privacy Lock": They haven't claimed their profile
            return !profileData.isVerified;
        }

        return false;
        
    };

    const isMasked = shouldMaskDetails();


    // --- 3. UPDATED CLICK HANDLER ---
    const handleActionClick = () => {
        if (!isMasked) return;

        if (profileData.profileType === 'Home') {
            // Scenario A: I am a business trying to access a Lead -> SHOW UPGRADE MODAL
            setShowUpgradeModal(true);
        } else {
            // Scenario B: I am viewing an unverified business -> SHOW ENQUIRY FORM
            navigate(`/contact-business?businessId=${profileData.id}`);
        }
    };


    const getMaskedAddress = (fullAddress) => {
        if (!fullAddress) return 'Location Hidden';
        // If Home user & Unsubscribed: Hide completely or just show Suburb
        if (profileData?.profileType === 'Home') {
            const suburbMatch = fullAddress.match(/([a-zA-Z\s]+),\s*(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)/i);
            if (suburbMatch) return `*****, ${suburbMatch[0]}`; // e.g. "***** Richmond, VIC"
            return 'Address Locked';
        }

        // Standard logic for businesses
        const postcodeMatch = fullAddress.match(/\b\d{4}\b/);
        const stateMatch = fullAddress.match(/\b(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)\b/i);
        const state = stateMatch ? stateMatch[0].toUpperCase() : '';
        const postcode = postcodeMatch ? postcodeMatch[0] : '';
        if (state || postcode) return `****, ${state} ${postcode}`;
        return '**** (Address Protected)';
    };

    const getMaskedPhone = (phone) => {
        if (!phone) return '04** *** ***';
        return `${phone.substring(0, 4)} *** ***`;
    };
    

    const getMaskedEmail = (email) => {
            if (!email) return 'Email Hidden';
            // Show first letter, mask the rest, keep top-level domain
            // Result: j***@***.com
            const parts = email.split('@');
            if (parts.length < 2) return 'Email Hidden'; 
            return `${parts[0][0]}***@***.${parts[1].split('.').pop() || 'com'}`;
        };

    const handleEnquiryRedirect = () => {
        navigate(`/contact-business?businessId=${profileData.id}`);
    };



    if (!activeConversation) {
        return <div className={styles.sidebarContainer} />;
    }

    return (
        <>

            {/* Show Upgrade Modal if they click a locked Home User contact */}
            {showUpgradeModal && (
                <UpgradeModal 
                    title="Unlock Homeowner Details"
                    message="Upgrade to a paid plan to view homeowner phone numbers and addresses immediately."
                    onClose={() => setShowUpgradeModal(false)}
                />
            )}

            <div className={styles.sidebarContainer}>
                <div className={styles.profileCard}>
                    {loading && <p className={styles.infoText}>Loading profile...</p>}
                    {error && <p className={styles.errorText}>{error}</p>}

                    {profileData && (
                        <>
                            {/* --- HEADER --- */}
                            <div className={styles.profileHeader}>
                                <div className={styles.headerActions}>
                                    <button onClick={handleToggleSearch} className={styles.headerActionButton} title="Search Messages">
                                        {isSearchActive ? <X size={20} /> : <Search size={20} />}
                                    </button>
                                    <button onClick={() => setIsProfileModalOpen(true)} className={styles.headerActionButton} title="View Profile">
                                        <User size={20} />
                                    </button>
                                </div>
                                <img src={profileData.profilePic || EmptyProfileImage} alt={profileData.name} className={styles.profileImage} />
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                    <h3 className={styles.businessName} title={profileData.name} style={{ marginBottom: 0 }}>{profileData.name}</h3>
                                    {profileData.profileType === 'Business' && profileData.isVerified && profileData.isDocApproved && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            backgroundColor: '#ECFDF5', border: '1px solid #6EE7B7',
                                            padding: '2px 8px', borderRadius: '12px'
                                        }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="#059669"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#059669' }}>Verified Pro</span>
                                        </div>
                                    )}
                                </div>
                                {profileData.tagline && !isSearchActive && (<p className={styles.description}>{profileData.tagline}</p>)}
                            </div>

                            {/* --- SEARCH PANEL --- */}
                            <div className={`${styles.searchPanel} ${isSearchActive ? styles.searchPanelActive : ''}`}>
                                <div className={styles.searchHeader}><h4 className={styles.sectionTitle}>Search Conversation</h4></div>
                                <form onSubmit={handleSearchSubmit}>
                                    <div className={styles.searchInputContainer}>
                                        <Search size={18} className={styles.searchInputIcon} />
                                        <input type="text" placeholder="Search messages..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className={styles.searchInput} />
                                    </div>
                                </form>
                                <div className={styles.searchResultsList}>
                                    {searchLoading && <p className={styles.infoText}>Searching...</p>}
                                    {!searchLoading && searchAttempted && filteredMessages.length === 0 && (<p className={styles.infoText}>No messages found.</p>)}
                                    {filteredMessages.map((msg) => (<div key={msg.messageId} className={styles.searchResultItem} onClick={() => { setChatSearching(msg); handleToggleSearch(); }}><div className={styles.resultHeader}><span className={styles.resultUser}>{msg.businessUserName || msg.homeUserName}</span><span className={styles.resultDate}>{new Date(msg.messageTime).toLocaleDateString()}</span></div><p className={styles.resultMessage}>{msg.message}</p></div>))}
                                </div>
                            </div>

                            {/* --- PROFILE CONTENT --- */}
                            <div className={`${styles.profileContent} ${isSearchActive ? styles.profileContentHidden : ''}`}>
                                {profileData.profileType === 'Business' && (
                                    <>
                                        <div className={styles.ratingsSection}>
                                            {profileData.reviewCount > 0 ? (
                                                <div className={styles.ratingItem}>
                                                    <StarRating ratings={profileData.rating} />
                                                    <span className={styles.ratingText}>
                                                        {profileData.rating?.toFixed(1)} ({profileData.reviewCount} {profileData.reviewCount === 1 ? 'review' : 'reviews'})
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className={styles.ratingItem}>
                                                    <span style={{ fontSize: '12px', background: '#eff6ff', color: '#2563eb', fontWeight: 600, padding: '4px 10px', borderRadius: '12px', display: 'inline-block' }}>
                                                        New Pro — No reviews yet
                                                    </span>
                                                </div>
                                            )}
                                            {profileData.googleRating && profileData.googleRatingCount > 0 && (
                                                <div className={styles.ratingItem}>
                                                    <span className={styles.googleRatingText}>
                                                        Google Rating: {profileData.googleRating.toFixed(1)} {formatReviewText(profileData.googleRatingCount)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {profileData.bio && (
                                            <div className={styles.bioSection}>
                                                <h4 className={styles.sectionTitle}>About Us</h4>
                                                <p className={styles.bioText}>{getBioText()}</p>
                                                {isBioLong && (
                                                    <button className={styles.readMoreButton} onClick={() => setIsBioExpanded(!isBioExpanded)}>
                                                        {isBioExpanded ? 'Show Less' : 'Read More'}
                                                        {isBioExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}

                                 {/* --- CONTACT SECTION --- */}
                                <div className={styles.contactSection}>
                                    <div className={styles.sectionHeader}>
                                        <h4 className={styles.sectionTitle}>Contact Information</h4>
                                        {isMasked && (
                                            <span className={styles.privacyBadge}>
                                                <Lock size={12} /> 
                                                {/* Text changes based on context */}
                                                {profileData.profileType === 'Home' ? 'Subscribers Only' : 'Privacy Protected'}
                                            </span>
                                        )}
                                    </div>

                                    <div className={styles.contactList}>
                                        {/* ADDRESS */}
                                        {profileData.address && (
                                            <div
                                                className={`${styles.contactItem} ${isMasked ? styles.lockedItem : ''}`}
                                                onClick={isMasked ? handleActionClick : undefined}
                                            >
                                                <MapPin size={18} className={styles.contactIcon} />
                                                <span className={styles.contactText}>
                                                    {!isMasked ? (
                                                        <a
                                                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(profileData.address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.contactLink}
                                                        >
                                                            {profileData.address}
                                                        </a>
                                                    ) : (
                                                        getMaskedAddress(profileData.address)
                                                    )}
                                                </span>
                                                {isMasked && <ChevronRight size={16} className={styles.actionArrow} />}
                                            </div>
                                        )}

                                        {/* PHONE */}
                                        {profileData.phone && (
                                            <div
                                                className={`${styles.contactItem} ${isMasked ? styles.lockedItem : ''}`}
                                                onClick={isMasked ? handleActionClick : undefined}
                                            >
                                                <Phone size={18} className={styles.contactIcon} />
                                                <span className={styles.contactText}>
                                                    {!isMasked ? (
                                                        <a href={`tel:${profileData.phone}`} className={styles.contactLink}>
                                                            {profileData.phone}
                                                        </a>
                                                    ) : (
                                                        getMaskedPhone(profileData.phone)
                                                    )}
                                                </span>
                                                {isMasked && <ChevronRight size={16} className={styles.actionArrow} />}
                                            </div>
                                        )}


                                        {profileData.email && (
                                            <div
                                                className={`${styles.contactItem} ${isMasked ? styles.lockedItem : ''}`}
                                                onClick={isMasked ? handleActionClick : undefined}
                                            >
                                                <Mail size={18} className={styles.contactIcon} />
                                                <span className={styles.contactText}>
                                                    {!isMasked ? (
                                                        <a href={`mailto:${profileData.email}`} className={styles.contactLink}>
                                                            {profileData.email}
                                                        </a>
                                                    ) : (
                                                        getMaskedEmail(profileData.email)
                                                    )}
                                                </span>
                                                {isMasked && <ChevronRight size={16} className={styles.actionArrow} />}
                                            </div>
                                        )}

                                    </div>
                                    
                                    {/* Call To Action Buttons */}
                                    {isMasked && (
                                        <div className={styles.enquiryWrapper}>
                                            <button 
                                                onClick={handleActionClick}
                                                className={styles.btnFullEnquiry}
                                                // Style differentiation: Gold for Upgrade, Blue for Enquiry
                                                style={profileData.profileType === 'Home' ? { background: '#d97706' } : {}}
                                            >
                                                {profileData.profileType === 'Home' ? 'Unlock Contact Details' : 'Make an Enquiry'}
                                            </button>
                                            <p className={styles.helperText}>
                                                {profileData.profileType === 'Home' 
                                                    ? 'Subscription required to view details.' 
                                                    : 'Connect securely to reveal details.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            {profileData?.profileType === 'Home' && (<HomeUserProfilePureCSS showMessageButton={false} isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} userId={profileData.id} />)}
            {profileData?.profileType === 'Business' && (<BusinessProfilePureCSS showMessageButton={false} isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} businessId={profileData.id} useIDTYPE={"BID"} />)}
        </>
    );
}

export default RightSidebar;